<template>
  <section class="dash wrap">
    <div class="dash__top">
      <div class="dash__filters">
        <div class="filter-group">
          <button
            v-for="option in rangeOptions"
            :key="option.value"
            class="filter-button"
            :class="{ active: selectedRange === option.value }"
            @click="setRange(option.value)"
          >
            {{ option.label }}
          </button>
        </div>
      </div>
    </div>

    <div class="kpis">
      <div class="kpi">
        <div class="kpi__label">PnL hoje</div>
        <div class="kpi__value" :class="pnlToday >= 0 ? 'pos' : 'neg'">
          {{ pnlToday >= 0 ? '+' : '' }}{{ fmtUSD(pnlToday) }}
        </div>
        <div class="kpi__hint">{{ todayChangePercentText }}</div>
      </div>
      <div class="kpi">
        <div class="kpi__label">PnL dos {{ rangeLabel }}</div>
        <div class="kpi__value" :class="pnlPeriodo >= 0 ? 'pos' : 'neg'">
          {{ pnlPeriodo >= 0 ? '+' : '' }}{{ fmtUSD(pnlPeriodo) }}
        </div>
        <div class="kpi__hint">{{ rangeSinceText }}</div>
      </div>
      <div class="kpi">
        <div class="kpi__label">Saldo</div>
        <div class="kpi__value">{{ fmtUSD(equity) }}</div>
        <div class="kpi__hint">Carteira Futures</div>
      </div>
      <div class="kpi">
        <div class="kpi__label">ROI</div>
        <div class="kpi__value" :class="roi >= 0 ? 'pos' : 'neg'">
          {{ roi.toFixed(2) }}%
        </div>
        <div class="kpi__hint">Baseado no saldo</div>
      </div>
    </div>

    <div v-if="isLoading" class="loading-banner">Carregando dados reais da conta...</div>
    <div v-if="errorMessage" class="error-banner">{{ errorMessage }}</div>

    <div class="grid2">
      <div class="card card--pnl-chart chart-card">
        <div class="card__head">
          <div class="card__title card-title-soft chart-section-title">
            P&amp;L acumulado (USD)
          </div>
        </div>
        <div class="pnl-chart__meta">
          <strong class="pnl-chart__meta-value" :class="chartCurrentValue >= 0 ? 'value-pos' : 'value-neg'">
            {{ chartCurrentValue >= 0 ? '+' : '' }}{{ fmtNumber(chartCurrentValue) }}
          </strong>
        </div>
        <div class="pnl-chart">
          <div class="pnl-chart__y-axis">
            <svg class="axis-svg" :viewBox="`0 0 36 ${chartHeight}`" role="presentation" aria-hidden="true">
              <text
                v-for="(row, index) in chartAxisRows"
                :key="`tick-${index}`"
                class="axis-text"
                x="34"
                :y="row.y"
                text-anchor="end"
                :dominant-baseline="index === 0 ? 'hanging' : index === chartAxisRows.length - 1 ? 'ideographic' : 'middle'"
              >
                {{ row.label }}
              </text>
            </svg>
          </div>
          <div class="pnl-chart__canvas">
            <svg class="pnl-chart__svg" :viewBox="`0 0 ${chartWidth} ${chartHeight}`" role="img" aria-label="P and L chart">
              <defs>
                <linearGradient id="pnlAreaPositive" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stop-color="#30c58d" stop-opacity="0.46" />
                  <stop offset="100%" stop-color="#30c58d" stop-opacity="0.08" />
                </linearGradient>
                <linearGradient id="pnlAreaNegative" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stop-color="#f05465" stop-opacity="0.42" />
                  <stop offset="100%" stop-color="#f05465" stop-opacity="0.1" />
                </linearGradient>
                <clipPath id="pnlClipTop">
                  <rect :x="0" :y="0" :width="chartWidth" :height="chartZeroY" />
                </clipPath>
                <clipPath id="pnlClipBottom">
                  <rect :x="0" :y="chartZeroY" :width="chartWidth" :height="chartHeight - chartZeroY" />
                </clipPath>
              </defs>

              <path class="pnl-chart__area-positive" :d="chartAreaPath" clip-path="url(#pnlClipTop)" />
              <path class="pnl-chart__area-negative" :d="chartAreaPath" clip-path="url(#pnlClipBottom)" />

              <path class="pnl-chart__line-positive" :d="chartLinePath" clip-path="url(#pnlClipTop)" />
              <path class="pnl-chart__line-negative" :d="chartLinePath" clip-path="url(#pnlClipBottom)" />
            </svg>
            <div class="pnl-chart__x-axis">
              <span>{{ chartStartLabel }}</span>
              <span>{{ chartEndLabel }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="card chart-card">
        <div class="card__title card-title-soft chart-section-title">P&amp;L acumulado(%)</div>
        <div class="pnlp-legend">
          <span class="pnlp-legend__item">
            <span class="pnlp-legend__dot" aria-hidden="true"></span>
            P&amp;L acumulado%:
            <strong :class="pnlPercentCurrent >= 0 ? 'value-pos' : 'value-neg'">
              {{ pnlPercentCurrent >= 0 ? '+' : '' }}{{ pnlPercentCurrent.toFixed(2) }}%
            </strong>
          </span>
        </div>
        <div class="pnlp-chart">
          <div class="pnlp-chart__y-axis">
            <svg class="axis-svg" :viewBox="`0 0 36 ${pnlPercentHeight}`" role="presentation" aria-hidden="true">
              <text
                v-for="(row, index) in pnlPercentAxisRows"
                :key="`pnlp-tick-${index}`"
                class="axis-text"
                x="34"
                :y="row.y"
                text-anchor="end"
                :dominant-baseline="index === 0 ? 'hanging' : index === pnlPercentAxisRows.length - 1 ? 'ideographic' : 'middle'"
              >
                {{ row.label }} %
              </text>
            </svg>
          </div>
          <div class="pnlp-chart__canvas">
            <svg class="pnlp-chart__svg" :viewBox="`0 0 ${pnlPercentWidth} ${pnlPercentHeight}`" role="img" aria-label="Cumulative P and L percent chart">
              <path class="pnlp-chart__line" :d="pnlPercentPath" />
            </svg>
            <div class="pnlp-chart__x-axis">
              <span>{{ pnlPercentStartLabel }}</span>
              <span>{{ pnlPercentEndLabel }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="grid2 grid2--details">
      <div class="card chart-card card--asset-trend">
        <div class="card__title card-title-soft chart-section-title">Tendência de ativos totais (USD)</div>
        <div class="asset-trend">
          <div class="asset-trend__y-axis">
            <svg class="axis-svg" :viewBox="`0 0 36 ${assetTrendHeight}`" role="presentation" aria-hidden="true">
              <text
                v-for="(row, index) in assetTrendAxisRows"
                :key="`asset-tick-${index}`"
                class="axis-text"
                x="34"
                :y="row.y"
                text-anchor="end"
                :dominant-baseline="index === 0 ? 'hanging' : index === assetTrendAxisRows.length - 1 ? 'ideographic' : 'middle'"
              >
                {{ row.label }}
              </text>
            </svg>
          </div>
          <div class="asset-trend__canvas">
            <svg class="asset-trend__svg" :viewBox="`0 0 ${assetTrendWidth} ${assetTrendHeight}`" role="img" aria-label="Tendência de ativos totais em USD">
              <path class="asset-trend__line" :d="assetTrendPath" />
            </svg>
            <div class="asset-trend__x-axis">
              <span>{{ assetTrendStartLabel }}</span>
              <span>{{ assetTrendEndLabel }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="card chart-card card--daily-details">
        <div class="card__title card-title-soft chart-section-title">Detalhes do PnL diário</div>
        <div class="list-head">
          <span>Resultado</span>
          <span>Data</span>
        </div>
        <ul class="list list--daily">
          <li v-for="row in dailyRowsForView" :key="`${row.date}-${row.pnl}`" class="list__row">
            <span class="list__pnl" :class="row.pnl >= 0 ? 'value-pos' : 'value-neg'">
              {{ row.pnl >= 0 ? '+' : '' }}{{ row.pnl.toFixed(2) }} USD
            </span>
            <span class="list__date">{{ fmtDateBR(row.date) }}</span>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<script>
import axios from 'axios';
import { API_BASE_URL } from '@/config/env';

export default {
  name: 'Dashboard',
  data() {
    return {
      rangeOptions: [
        { label: 'Últimos 7D', value: '7D' },
        { label: 'Últimos 30D', value: '30D' },
        { label: 'Últimos 90D', value: '90D' },
        { label: 'Últimos 180D', value: '180D' }
      ],
      selectedRange: '7D',
      equity: 0,
      pnlToday: 0,
      pnlPeriodo: 0,
      roi: 0,
      lastDate: '-',
      daily: [],
      isLoading: false,
      errorMessage: ''
    };
  },
  computed: {
    rangeLabel() {
      const map = { '7D': '7 dias', '30D': '30 dias', '90D': '90 dias', '180D': '180 dias' };
      return map[this.selectedRange] ?? this.selectedRange;
    },
    todayChangePercent() {
      const base = this.equity - this.pnlToday;
      if (!base || !isFinite(base)) {
        return 0;
      }
      return (this.pnlToday / base) * 100;
    },
    todayChangePercentText() {
      const value = this.todayChangePercent;
      const sign = value >= 0 ? '+' : '';
      return `${sign}${value.toFixed(2)}%`;
    },
    rangeSinceText() {
      const days = this.rangeToDays();
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const start = new Date(now);
      start.setDate(now.getDate() - (days - 1));
      const formatter = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
      return `Desde ${formatter.format(start)}`;
    },
    chartWidth() {
      return 860;
    },
    chartHeight() {
      return 232;
    },
    chartPlotPadding() {
      return 12;
    },
    pnlChartData() {
      if (!Array.isArray(this.daily) || !this.daily.length) {
        return [];
      }

      let cumulative = 0;
      return this.daily.map((row) => {
        cumulative += Number(row?.pnl) || 0;
        return {
          date: row?.date || '-',
          value: Number(cumulative.toFixed(2))
        };
      });
    },
    chartMin() {
      const values = this.pnlChartData.map((point) => point.value).concat(0);
      const min = Math.min(...values);
      const max = Math.max(...values);
      const span = Math.max(1, max - min);
      return min - span * 0.08;
    },
    chartMax() {
      const values = this.pnlChartData.map((point) => point.value).concat(0);
      const min = Math.min(...values);
      const max = Math.max(...values);
      const span = Math.max(1, max - min);
      return max + span * 0.08;
    },
    chartRange() {
      return Math.max(1, this.chartMax - this.chartMin);
    },
    chartZeroY() {
      const y = this.valueToY(0, this.chartMax, this.chartRange, this.chartHeight, this.chartPlotPadding);
      return Math.max(0, Math.min(this.chartHeight, y));
    },
    chartPlotPoints() {
      const total = this.pnlChartData.length;
      return this.pnlChartData.map((point, index) => ({
        x: this.indexToX(index, total),
        y: this.valueToY(point.value, this.chartMax, this.chartRange, this.chartHeight, this.chartPlotPadding)
      }));
    },
    chartLinePath() {
      if (!this.chartPlotPoints.length) {
        return '';
      }

      let path = `M ${this.chartPlotPoints[0].x} ${this.chartPlotPoints[0].y}`;
      for (let i = 1; i < this.chartPlotPoints.length; i += 1) {
        const point = this.chartPlotPoints[i];
        path += ` H ${point.x} V ${point.y}`;
      }
      return path;
    },
    chartAreaPath() {
      if (!this.chartPlotPoints.length) {
        return '';
      }

      const first = this.chartPlotPoints[0];
      const baseY = this.chartZeroY;
      let path = `M ${first.x} ${baseY} V ${first.y}`;
      for (let i = 1; i < this.chartPlotPoints.length; i += 1) {
        const point = this.chartPlotPoints[i];
        path += ` H ${point.x} V ${point.y}`;
      }
      path += ` V ${baseY} H ${first.x} Z`;
      return path;
    },
    chartGridLines() {
      const rows = 5;
      const drawable = this.chartHeight - this.chartPlotPadding * 2;
      return Array.from({ length: rows }, (_, index) => this.chartPlotPadding + (drawable / (rows - 1)) * index);
    },
    chartTicks() {
      const rows = 5;
      const step = this.chartRange / (rows - 1);
      return Array.from({ length: rows }, (_, index) => {
        const value = this.chartMax - step * index;
        return Math.round(value);
      });
    },
    chartStartLabel() {
      return this.pnlChartData[0]?.date || '-';
    },
    chartEndLabel() {
      return this.pnlChartData[this.pnlChartData.length - 1]?.date || '-';
    },
    chartCurrentValue() {
      return this.pnlChartData[this.pnlChartData.length - 1]?.value || 0;
    },
    chartAxisRows() {
      return this.chartGridLines.map((y, index) => ({
        y,
        label: this.chartTicks[index]
      }));
    },
    pnlPercentWidth() {
      return 860;
    },
    pnlPercentHeight() {
      return 232;
    },
    pnlPercentPlotPadding() {
      return 12;
    },
    pnlPercentData() {
      if (!Array.isArray(this.daily) || !this.daily.length) {
        return [];
      }

      const base = Math.max(1, this.equity - this.pnlPeriodo);
      let cumulative = 0;
      return this.daily.map((row) => {
        cumulative += Number(row?.pnl) || 0;
        return {
          date: row?.date || '-',
          value: Number(((cumulative / base) * 100).toFixed(2))
        };
      });
    },
    pnlPercentCurrent() {
      return this.pnlPercentData[this.pnlPercentData.length - 1]?.value || 0;
    },
    pnlPercentMin() {
      const values = this.pnlPercentData.map((point) => point.value).concat(0);
      const min = Math.min(...values);
      const max = Math.max(...values);
      const span = Math.max(1, max - min);
      return min - span * 0.12;
    },
    pnlPercentMax() {
      const values = this.pnlPercentData.map((point) => point.value).concat(0);
      const min = Math.min(...values);
      const max = Math.max(...values);
      const span = Math.max(1, max - min);
      return max + span * 0.12;
    },
    pnlPercentRange() {
      return Math.max(1, this.pnlPercentMax - this.pnlPercentMin);
    },
    pnlPercentPlotPoints() {
      const total = this.pnlPercentData.length;
      return this.pnlPercentData.map((point, index) => ({
        x: this.indexToX(index, total, this.pnlPercentWidth),
        y: this.valueToY(point.value, this.pnlPercentMax, this.pnlPercentRange, this.pnlPercentHeight, this.pnlPercentPlotPadding)
      }));
    },
    pnlPercentPath() {
      if (!this.pnlPercentPlotPoints.length) {
        return '';
      }

      let path = `M ${this.pnlPercentPlotPoints[0].x} ${this.pnlPercentPlotPoints[0].y}`;
      for (let i = 1; i < this.pnlPercentPlotPoints.length; i += 1) {
        const point = this.pnlPercentPlotPoints[i];
        path += ` L ${point.x} ${point.y}`;
      }
      return path;
    },
    pnlPercentZeroY() {
      const y = this.valueToY(0, this.pnlPercentMax, this.pnlPercentRange, this.pnlPercentHeight, this.pnlPercentPlotPadding);
      return Math.max(0, Math.min(this.pnlPercentHeight, y));
    },
    pnlPercentGridLines() {
      const rows = 6;
      const drawable = this.pnlPercentHeight - this.pnlPercentPlotPadding * 2;
      return Array.from({ length: rows }, (_, index) => this.pnlPercentPlotPadding + (drawable / (rows - 1)) * index);
    },
    pnlPercentTicks() {
      const rows = 6;
      const step = this.pnlPercentRange / (rows - 1);
      return Array.from({ length: rows }, (_, index) => {
        const value = this.pnlPercentMax - step * index;
        return value.toFixed(0);
      });
    },
    pnlPercentStartLabel() {
      return this.pnlPercentData[0]?.date || '-';
    },
    pnlPercentEndLabel() {
      return this.pnlPercentData[this.pnlPercentData.length - 1]?.date || '-';
    },
    pnlPercentAxisRows() {
      return this.pnlPercentGridLines.map((y, index) => ({
        y,
        label: this.pnlPercentTicks[index]
      }));
    },
    assetTrendWidth() {
      return 860;
    },
    assetTrendHeight() {
      return 232;
    },
    assetTrendPlotPadding() {
      return 12;
    },
    assetTrendData() {
      if (!Array.isArray(this.daily) || !this.daily.length) {
        return [];
      }

      const start = Math.max(1, this.equity - this.pnlPeriodo);
      let cumulative = 0;
      return this.daily.map((row) => {
        cumulative += Number(row?.pnl) || 0;
        return {
          date: row?.date || '-',
          value: Number((start + cumulative).toFixed(2))
        };
      });
    },
    assetTrendMin() {
      const values = this.assetTrendData.map((point) => point.value).concat(0);
      const min = Math.min(...values);
      const max = Math.max(...values);
      const span = Math.max(1, max - min);
      return min - span * 0.1;
    },
    assetTrendMax() {
      const values = this.assetTrendData.map((point) => point.value).concat(0);
      const min = Math.min(...values);
      const max = Math.max(...values);
      const span = Math.max(1, max - min);
      return max + span * 0.08;
    },
    assetTrendRange() {
      return Math.max(1, this.assetTrendMax - this.assetTrendMin);
    },
    assetTrendPlotPoints() {
      const total = this.assetTrendData.length;
      return this.assetTrendData.map((point, index) => ({
        x: this.indexToX(index, total, this.assetTrendWidth),
        y: this.valueToY(point.value, this.assetTrendMax, this.assetTrendRange, this.assetTrendHeight, this.assetTrendPlotPadding)
      }));
    },
    assetTrendPath() {
      if (!this.assetTrendPlotPoints.length) {
        return '';
      }

      let path = `M ${this.assetTrendPlotPoints[0].x} ${this.assetTrendPlotPoints[0].y}`;
      for (let i = 1; i < this.assetTrendPlotPoints.length; i += 1) {
        const point = this.assetTrendPlotPoints[i];
        path += ` H ${point.x} V ${point.y}`;
      }
      return path;
    },
    assetTrendGridLines() {
      const rows = 6;
      const drawable = this.assetTrendHeight - this.assetTrendPlotPadding * 2;
      return Array.from({ length: rows }, (_, index) => this.assetTrendPlotPadding + (drawable / (rows - 1)) * index);
    },
    assetTrendTicks() {
      const rows = 6;
      const step = this.assetTrendRange / (rows - 1);
      return Array.from({ length: rows }, (_, index) => {
        const value = this.assetTrendMax - step * index;
        return Math.round(value);
      });
    },
    assetTrendStartLabel() {
      return this.assetTrendData[0]?.date || '-';
    },
    assetTrendEndLabel() {
      return this.assetTrendData[this.assetTrendData.length - 1]?.date || '-';
    },
    assetTrendAxisRows() {
      return this.assetTrendGridLines.map((y, index) => ({
        y,
        label: this.assetTrendTicks[index]
      }));
    },
    dailyRowsForView() {
      return Array.isArray(this.daily) ? this.daily : [];
    }
  },
  watch: {
    selectedRange() {
      this.fetchOverview();
    },
  },
  mounted() {
    this.fetchOverview();
  },
  methods: {
    indexToX(index, total, width = this.chartWidth) {
      if (total <= 1) {
        return 0;
      }
      return (index / (total - 1)) * width;
    },
    valueToY(value, max = this.chartMax, range = this.chartRange, height = this.chartHeight, padding = 0) {
      const drawable = Math.max(1, height - padding * 2);
      return padding + ((max - value) / range) * drawable;
    },
    setRange(value) {
      this.selectedRange = value;
    },
    rangeToDays() {
      const map = { '7D': 7, '30D': 30, '90D': 90, '180D': 180 };
      return map[this.selectedRange] || 7;
    },
    async fetchOverview() {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        this.errorMessage = 'Faça login para carregar dados da Bybit.';
        return;
      }

      this.isLoading = true;
      this.errorMessage = '';

      try {
        const response = await axios.get(`${API_BASE_URL}/api/v1/bybit/overview`, {
          params: { range_days: this.rangeToDays() },
          headers: { Authorization: `Bearer ${token}` }
        });

        const data = response.data || {};
        this.equity = Number(data.equity) || 0;
        this.pnlToday = Number(data.pnl_today) || 0;
        this.pnlPeriodo = Number(data.pnl_period) || 0;
        this.roi = Number(data.roi) || 0;
        this.lastDate = data.last_date || '-';
        this.daily = Array.isArray(data.daily) ? data.daily : [];
      } catch (error) {
        console.error('Erro ao carregar dashboard:', error);
        this.errorMessage =
          error?.response?.data?.error ||
          'Erro ao carregar dados da Bybit.';
      } finally {
        this.isLoading = false;
      }
    },
    fmtUSD(n) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(n);
    },
    fmtNumber(n) {
      return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
    },
    fmtDateBR(value) {
      if (!value) {
        return '-';
      }

      const date = new Date(`${value}T00:00:00`);
      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(date);
    }
  }
};
</script>

<style scoped>
.wrap { max-width: 1100px; margin: 0 auto; padding: 96px var(--app-gutter) 40px; color: #e6e8ec; padding-top: calc(var(--app-header-h) + 20px); }
.dash { background: transparent; position: relative; isolation: isolate; min-height: 100vh; overflow: hidden; }
.dash::before {
  content: '';
  position: fixed;
  inset: 0;
  background: radial-gradient(circle at top left, rgba(78, 115, 223, 0.12), transparent 45%),
    radial-gradient(circle at top right, rgba(155, 89, 182, 0.14), transparent 40%),
    #11131a;
  z-index: 0;
  pointer-events: none;
}
.dash::after {
  content: '';
  position: fixed;
  inset: 0;
  background-image: linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
  background-size: 32px 32px;
  opacity: 0.4;
  z-index: 0;
  pointer-events: none;
}
.dash > * { position: relative; z-index: 1; }
.dash__top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.dash__filters { display: flex; gap: 12px; flex-wrap: wrap; align-items: center; }
.filter-group {
  display: inline-flex;
  gap: 12px;
  padding: 4px;
  border-radius: 999px;
  background: rgba(20, 26, 38, 0.6);
  border: 1px solid rgba(115, 148, 240, 0.25);
  backdrop-filter: blur(8px);
}

.filter-button {
  min-width: 120px;
  padding: 10px 18px;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: rgba(226, 232, 255, 0.75);
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-button:hover,
.filter-button.active {
  background: linear-gradient(120deg, rgba(78, 115, 223, 0.9), rgba(155, 89, 182, 0.9));
  color: #fff;
  box-shadow: 0 0 14px rgba(115, 148, 240, 0.4);
}

@media (max-width: 767px) {
  .dash__top {
    margin-bottom: 12px;
  }

  .dash__filters {
    width: 100%;
    display: grid;
    gap: 8px;
  }

  .filter-group {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    padding: 6px;
    border-radius: 14px;
    overflow: visible;
  }

  .filter-button {
    min-width: 0;
    width: 100%;
    padding: 9px 8px;
    font-size: 0.7rem;
    letter-spacing: 0.06em;
    line-height: 1.2;
    white-space: normal;
  }

}

.kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
  margin-bottom: 16px;
  font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
}
.kpi {
  position: relative;
  border-radius: 22px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  background: #0b0d14;
  box-shadow: none;
  overflow: hidden;
}
.kpi::before {
  content: '';
  position: absolute;
  inset: 0 0 auto;
  height: 3px;
  background: linear-gradient(120deg, #4e73df, #9b59b6);
  border-radius: 999px;
  z-index: 2;
}
.kpi::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(160deg, rgba(78, 115, 223, 0.12), rgba(155, 89, 182, 0.15));
  pointer-events: none;
  opacity: 0.5;
  z-index: 0;
}
.kpi__label {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.25em;
  margin-bottom: 8px;
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
}
.kpi__value {
  font-family: 'Archivo', 'Space Grotesk', 'Inter', sans-serif;
  font-weight: 700;
  font-size: 28px;
  letter-spacing: 0.04em;
}
.kpi__value.pos {
  color: #4efcaf;
}
.kpi__value.neg {
  color: #ff7c8f;
}
.kpi__hint {
  color: rgba(169, 183, 220, 0.9);
  font-size: 0.85rem;
  margin-top: 8px;
  font-weight: 500;
  letter-spacing: 0.08em;
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
}
.kpi > * {
  position: relative;
  z-index: 3;
}
.pos { color: #38d39f; }
.neg { color: #ff6b6b; }

.grid2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; margin-bottom: 16px; align-items: start; }
.grid2--details { align-items: stretch; }
.grid2--details .card {
  height: 370px;
  min-height: 370px;
  max-height: 370px;
  overflow: hidden;
}
.card {
  position: relative;
  border-radius: 22px;
  padding: 14px;
  min-height: 260px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  background: #0b0d14;
  box-shadow: none;
  overflow: hidden;
}
.card::before {
  content: '';
  position: absolute;
  inset: 0 0 auto;
  height: 3px;
  background: linear-gradient(120deg, #4e73df, #9b59b6);
  border-radius: 999px;
  z-index: 2;
}
.card::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(160deg, rgba(78, 115, 223, 0.12), rgba(155, 89, 182, 0.15));
  pointer-events: none;
  opacity: 0.32;
  z-index: 0;
}
.card > * {
  position: relative;
  z-index: 3;
}
.card__title { color: #9aa3b2; font-size: 13px; margin-bottom: 8px; font-weight: 700; }
.card-title-soft { color: rgba(255, 255, 255, 0.8); }
.chart-section-title {
  display: inline-flex;
  align-items: center;
  margin-bottom: 0;
  font-size: 1rem;
  line-height: 1.1;
  font-weight: 700;
}
.card__chart { height: 220px; display: flex; align-items: center; justify-content: center; color: #7d8596; background: rgba(255,255,255,0.03); border-radius: 10px; border: 1px dashed #2a2f3a; }
.empty { height: 220px; display: flex; align-items: center; justify-content: center; color: #7d8596; background: rgba(255,255,255,0.03); border-radius: 10px; border: 1px dashed #2a2f3a; }
.chart-card,
.chart-card * {
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
}
.chart-card {
  color: rgba(169, 183, 220, 0.9);
}
.card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.card--pnl-chart {
  min-height: 0;
}

.pnl-chart {
  margin-top: 10px;
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 8px;
  align-items: stretch;
  border: 1px solid rgba(138, 149, 178, 0.2);
  border-radius: 12px;
  padding: 10px 12px 10px 8px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0));
}

.pnl-chart__y-axis {
  height: 232px;
  display: block;
  color: #7f8aa1;
  font-size: 10px;
  width: 36px;
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
  line-height: 1;
  padding-right: 4px;
}

.pnl-chart__canvas {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.pnl-chart__svg {
  width: 100%;
  height: 232px;
  border-radius: 8px;
  background: transparent;
}

.pnl-chart__area-positive {
  fill: url(#pnlAreaPositive);
}

.pnl-chart__area-negative {
  fill: url(#pnlAreaNegative);
}

.pnl-chart__line-positive {
  fill: none;
  stroke: #34c58f;
  stroke-width: 3;
  stroke-linejoin: round;
  stroke-linecap: round;
}

.pnl-chart__line-negative {
  fill: none;
  stroke: #f05465;
  stroke-width: 3;
  stroke-linejoin: round;
  stroke-linecap: round;
}

.pnl-chart__x-axis {
  color: rgba(169, 183, 220, 0.9);
  font-size: 11px;
  display: flex;
  justify-content: space-between;
  padding: 0 2px;
}

.pnl-chart__meta {
  margin-top: 2px;
  margin-bottom: 8px;
  min-height: 18px;
}

.pnl-chart__meta-value {
  font-size: 1.45rem;
  line-height: 1;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: rgba(169, 183, 220, 0.9);
}

.value-pos { color: #38d39f; }
.value-neg { color: #ff6b6b; }

.list { list-style: none; padding: 0; margin: 0; }
.list-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 10px 6px 2px;
  color: rgba(169, 183, 220, 0.75);
  font-size: 0.68rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.list-head span:last-child {
  padding-right: 10px;
}
.list--daily {
  max-height: 362px;
  overflow: auto;
  padding-right: 10px;
  scrollbar-gutter: stable;
}
.list--daily::-webkit-scrollbar {
  width: 6px;
}
.list--daily::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(138, 149, 178, 0.35);
}
.list--daily::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.03);
}
.list__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  margin-bottom: 5px;
  border: 1px solid rgba(138, 149, 178, 0.14);
  border-radius: 10px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0));
  font-size: 0.86rem;
}
.list__date {
  color: rgba(169, 183, 220, 0.9);
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
}
.list__pnl {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
}

.card--daily-details {
  display: flex;
  flex-direction: column;
}

.card--asset-trend {
  display: flex;
  flex-direction: column;
}

.card--asset-trend .asset-trend {
  flex: 1 1 auto;
  min-height: 0;
}

.card--asset-trend .asset-trend__y-axis {
  height: auto;
}

.card--asset-trend .asset-trend__canvas {
  flex: 1 1 auto;
  min-height: 0;
}

.card--asset-trend .asset-trend__svg {
  flex: 1 1 auto;
  height: auto;
  min-height: 0;
}

.card--daily-details .list-head {
  flex: 0 0 auto;
}

.card--daily-details .list--daily {
  flex: 1 1 auto;
  min-height: 0;
  max-height: none;
  overflow: auto;
}

.pnlp-legend {
  margin-top: 10px;
  margin-bottom: 10px;
  color: rgba(169, 183, 220, 0.9);
  font-size: 12px;
}
.pnlp-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
}
.pnlp-legend__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #f3a239;
  box-shadow: 0 0 0 3px rgba(243, 162, 57, 0.12);
}

.pnlp-chart {
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 8px;
  align-items: stretch;
  border: 1px solid rgba(138, 149, 178, 0.2);
  border-radius: 12px;
  padding: 10px 12px 10px 8px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0));
}

.pnlp-chart__y-axis {
  height: 232px;
  display: block;
  color: #7f8aa1;
  font-size: 10px;
  width: 36px;
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
  line-height: 1;
  padding-right: 4px;
}

.pnlp-chart__canvas {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.pnlp-chart__svg {
  width: 100%;
  height: 232px;
  border-radius: 8px;
  background: transparent;
}

.pnlp-chart__line {
  fill: none;
  stroke: #4f95ff;
  stroke-width: 2.6;
  stroke-linejoin: round;
  stroke-linecap: round;
}

.pnlp-chart__x-axis {
  color: rgba(169, 183, 220, 0.9);
  font-size: 11px;
  display: flex;
  justify-content: space-between;
  padding: 0 2px;
}

.asset-trend {
  margin-top: 10px;
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 8px;
  align-items: stretch;
  border: 1px solid rgba(138, 149, 178, 0.2);
  border-radius: 12px;
  padding: 10px 12px 10px 8px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0));
}

.asset-trend__y-axis {
  height: 232px;
  display: block;
  color: #7f8aa1;
  font-size: 10px;
  width: 36px;
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
  line-height: 1;
  padding-right: 4px;
}

.axis-svg {
  width: 100%;
  height: 100%;
  display: block;
}

.axis-text {
  fill: rgba(169, 183, 220, 0.9);
  font-size: 10px;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
}

.asset-trend__canvas {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.asset-trend__svg {
  width: 100%;
  height: 232px;
  border-radius: 8px;
  background: transparent;
}

.asset-trend__line {
  fill: none;
  stroke: #f2a625;
  stroke-width: 2.7;
  stroke-linejoin: round;
  stroke-linecap: round;
}

.asset-trend__x-axis {
  color: rgba(169, 183, 220, 0.9);
  font-size: 11px;
  display: flex;
  justify-content: space-between;
  padding: 0 2px;
}

.loading-banner,
.error-banner {
  margin: 10px 0 16px;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
}

.loading-banner {
  background: rgba(78, 115, 223, 0.15);
  color: #c9d5ff;
  border: 1px solid rgba(78, 115, 223, 0.35);
}

.error-banner {
  background: rgba(255, 107, 107, 0.12);
  color: #ffb0b0;
  border: 1px solid rgba(255, 107, 107, 0.35);
}

@media (max-width: 1024px) {
  .kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 10px;
  }

  .kpi {
    padding: 10px 12px;
    border-radius: 14px;
  }

  .kpi__label {
    font-size: 0.62rem;
    letter-spacing: 0.14em;
    margin-bottom: 4px;
  }

  .kpi__value {
    font-size: 1.15rem;
    line-height: 1.1;
  }

  .kpi__hint {
    font-size: 0.62rem;
    margin-top: 4px;
    letter-spacing: 0.04em;
  }
}

@media (max-width: 1199px) {
  .grid2 { grid-template-columns: 1fr; }

  .grid2--details .card {
    height: auto;
    min-height: 0;
    max-height: none;
  }
}

@media (max-width: 767px) {
  .kpis {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .kpi {
    padding: 16px;
    border-radius: 16px;
  }

  .kpi__label {
    font-size: 0.68rem;
    letter-spacing: 0.18em;
    margin-bottom: 6px;
  }

  .kpi__value {
    font-size: 2rem;
  }

  .kpi__hint {
    font-size: 0.78rem;
    margin-top: 6px;
  }

  .pnl-chart {
    grid-template-columns: 32px 1fr;
    gap: 4px;
    padding: 8px 8px 8px 6px;
  }

  .pnl-chart__y-axis {
    width: 32px;
    padding-right: 3px;
  }

  .pnl-chart__x-axis {
    font-size: 10px;
  }

  .pnl-chart__meta-value {
    font-size: 1.2rem;
  }

  .pnlp-chart {
    grid-template-columns: 32px 1fr;
    gap: 4px;
    padding: 8px 8px 8px 6px;
  }

  .pnlp-chart__y-axis {
    width: 32px;
    padding-right: 3px;
  }

  .pnlp-chart__x-axis {
    font-size: 10px;
  }

  .asset-trend {
    grid-template-columns: 32px 1fr;
    gap: 4px;
    padding: 8px 8px 8px 6px;
  }

  .asset-trend__y-axis {
    width: 32px;
    padding-right: 3px;
  }

  .axis-text {
    font-size: 9px;
  }

  .asset-trend__x-axis {
    font-size: 10px;
  }

  .list--daily {
    max-height: 300px;
  }
}
</style>
